import React, { Component } from "react"
import { translate } from "react-i18next"
import styled from "styled-components"
import { connect } from "react-redux"
import { compose } from "redux"
import PropTypes from "prop-types"
import FilterGroup from "./FilterGroup"
import ResetAllFiltersButton from "./ResetAllFiltersButton"
import FilterField from "./FilterField"
import FilterLoadingSpinner from "./FilterLoadingSpinner"
import LocationPicker from "./LocationPicker"
import ManagerFilter from "./ManagerFilter"
import BackToTalentToolboxLink from "../layout/navigation/BackToTalentToolboxLink"
import { Hyperlink } from "../themed-html-components"
import { ENVIRONMENT } from "../../constants"
import { media } from "../../utils/style-utils"
import * as filterActions from "../../state/actions/filterActions"
import { ClientFeatureSettings } from "../../settings/clientFeatureSettings"
import { CheckboxList, RadioList, DatePicker } from "../inputs"

const MainHeading = styled.h3`
  text-transform: uppercase;
  font-size: 14px;
  color: ${props => props.primaryColor};
  font-weight: bold;
  float: left;
`

const MobileHideFiltersContainer = styled.div`
  display: ${props => (props.mobileFiltersVisible ? "block" : "none")};
  ${media.tablet`display: block;`};
  text-align: center;
  margin-top: 20px;

  & button {
    border: 0;
    background-color: transparent;
    color: ${props => props.primaryColor};
    font-size: 12px;
  }

  & button:hover {
    cursor: pointer;
    text-decoration: underline;
  }

  & button:focus {
    outline: 0;
  }
`

const UnPinFiltersButton = styled.button`
  ${media.tablet`display: none;`};
  text-align: center;
  margin-top: 10px;
  margin-left: -5px;
  background: transparent;
  color: #b3b3b3;
  font-size: 12px;
  text-align: center;
  border: 0;
  &:hover {
    cursor: pointer;
    color: #777;
  }
  &:focus {
    outline: 0;
  }
`

const FooterLinkLeft = styled.div`
  margin-right: 20px;
  font-size: 14px;
`

const BackToTtLink = styled(BackToTalentToolboxLink)`
  color: ${props => props.theme.primaryColor} !important;
  &:hover {
    color: ${props => props.theme.primaryColorHover} !important;
  }
`

class ReportFilters extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired
  }

  closeFilters = () => {
    this.props.toggleFullScreenFilters(false) // eslint-disable-line react/prop-types
  }

  handleResetFiltersClick = () => {
    this.props.resetFilters() // eslint-disable-line react/prop-types
    this.props.configureViewModeForUser(this.props.userState) // eslint-disable-line react/prop-types
  }

  toggleAdvancedFiltersExpand = () => {
    // eslint-disable-next-line react/prop-types
    this.props.setAdvancedFiltersOpen(!this.props.filterState.advancedFiltersOpen)
  }

  handleAppraisalLevelsValueChange = (selectedValues, displayText, scrollTop) => {
    // eslint-disable-next-line react/prop-types
    this.props.changeValueAppraisalLevels(selectedValues, displayText, scrollTop)
  }

  handleDataModeValueChange = selectedValue => {
    // eslint-disable-next-line react/prop-types
    this.props.changeValueDataMode(selectedValue)
  }

  handleEndDateValueChange = newValue => {
    this.props.changeValueEndDate(newValue) // eslint-disable-line react/prop-types
  }

  handleGenerationsValueChange = (selectedValues, displayText, scrollTop) => {
    // eslint-disable-next-line react/prop-types
    this.props.changeValueGenerations(selectedValues, displayText, scrollTop)
  }

  handleGendersValueChange = (selectedValues, displayText, scrollTop) => {
    // eslint-disable-next-line react/prop-types
    this.props.changeValueGenders(selectedValues, displayText, scrollTop)
  }

  handleJobTitlesValueChange = (selectedValues, displayText, scrollTop) => {
    // eslint-disable-next-line react/prop-types
    this.props.changeValueJobTitles(selectedValues, displayText, scrollTop)
  }

  handleReviewValueChange = newValue => {
    var shouldEnforceStatic = this.props.filterState.fields.review.options.find(
      x => x.value === newValue
    ).isStaticOnly
    this.props.changeValueReview(newValue) // eslint-disable-line react/prop-types

    if (shouldEnforceStatic) {
      this.props.changeValueDataMode("STATIC")
    }
  }

  handleStartDateValueChange = newValue => {
    this.props.changeValueStartDate(newValue) // eslint-disable-line react/prop-types
  }

  handleViewModeValueChange = selectedValue => {
    // eslint-disable-next-line react/prop-types
    this.props.changeValueViewMode(selectedValue) // eslint-disable-line react/prop-types
  }
  handleManagerFilterChange = selectedValue => {
    if (selectedValue && selectedValue.employeeId > 0) {
      this.props.changeManagerFilter(selectedValue) // eslint-disable-line react/prop-types
    } else {
      this.props.clearManagerFilter() // eslint-disable-line react/prop-types
    }
  }
  render() {
    const { t, filterState, userState, toggleFiltersPinned } = this.props
    const showGenderFilter = ClientFeatureSettings.GenderFilter.some(x => x === userState.client.id);

    let selectedLocationIds = []
    if (
      filterState.fields.locations.selectedValue &&
      filterState.fields.locations.selectedValue.length > 0
    ) {
      selectedLocationIds = filterState.fields.locations.selectedValue
        .split(",")
        .map(x => parseInt(x)) // eslint-disable-line radix
    }

    const standardFilters = (
      <FilterGroup>
        <FilterField
          labelKey="Filters.Standard.DataMode.Name"
          tooltipKey="Filters.Standard.DataMode.Tooltip"
          filter={filterState.fields.dataMode}
        >
          <RadioList
            options={filterState.fields.dataMode.options}
            onChange={this.handleDataModeValueChange}
            translateLabels
          />
        </FilterField>

        <FilterField
          labelKey="Filters.Standard.ViewMode.Name"
          tooltipKey="Filters.Standard.ViewMode.Tooltip"
          filter={filterState.fields.viewMode}
        >
          {filterState.fields.viewMode.isLoading ? (
            <FilterLoadingSpinner />
          ) : (
            <RadioList
              options={filterState.fields.viewMode.options}
              onChange={this.handleViewModeValueChange}
              translateLabels
            />
          )}
        </FilterField>

        {/* Hide the reviews filter if the client doesn't have any reviews */}
        {(filterState.fields.review.isLoading || filterState.fields.review.options.length > 0) && (
          <FilterField
            labelKey="Filters.Standard.Review.Name"
            tooltipKey="Filters.Standard.Review.Tooltip"
            filter={filterState.fields.review}
          >
            {filterState.fields.review.isLoading ? (
              <FilterLoadingSpinner />
            ) : (
              <RadioList
                options={filterState.fields.review.options}
                onChange={this.handleReviewValueChange}
                sortAlphabetically
                translateLabels
              />
            )}
          </FilterField>
        )}

        <FilterField
          labelKey="Filters.Standard.StartDate.Name"
          tooltipKey="Filters.Standard.StartDate.Tooltip"
          filter={filterState.fields.startDate}
        >
          <DatePicker
            autoFocus
            onChange={this.handleStartDateValueChange}
            maxValue={filterState.fields.startDate.maxValue}
            minValue={filterState.fields.startDate.minValue}
            selectedDate={filterState.fields.startDate.selectedValue}
          />
        </FilterField>

        <FilterField
          labelKey="Filters.Standard.EndDate.Name"
          tooltipKey="Filters.Standard.EndDate.Tooltip"
          filter={filterState.fields.endDate}
        >
          <DatePicker
            autoFocus
            onChange={this.handleEndDateValueChange}
            maxValue={filterState.fields.endDate.maxValue}
            minValue={filterState.fields.endDate.minValue}
            selectedDate={filterState.fields.endDate.selectedValue}
          />
        </FilterField>

        <FilterField
          labelKey="Filters.Standard.AppraisalLevels.Name"
          tooltipKey="Filters.Standard.AppraisalLevels.Tooltip"
          filter={filterState.fields.appraisalLevels}
          multipleChoice
        >
          {filterState.fields.appraisalLevels.isLoading ? (
            <FilterLoadingSpinner />
          ) : (
            <CheckboxList
              options={filterState.fields.appraisalLevels.options}
              onChange={this.handleAppraisalLevelsValueChange}
              scrollTop={filterState.fields.appraisalLevels.scrollTop}
              translateLabels
              sortAlphabetically
            />
          )}
        </FilterField>

        <FilterField
          labelKey="Filters.Standard.Locations.Name"
          tooltipKey="Filters.Standard.Locations.Tooltip"
          filter={filterState.fields.locations}
          multipleChoice
        >
          {filterState.fields.locations.isLoading ? (
            <FilterLoadingSpinner />
          ) : (
            filterState.activeField === "Filters.Standard.Locations.Name" && (
              <LocationPicker
                treeData={filterState.fields.locations.options}
                selectedValues={selectedLocationIds}
              />
            )
          )}
        </FilterField>
      </FilterGroup>
    )

    const advancedFilters = (
      <FilterGroup
        expandable
        expanded={filterState.advancedFiltersOpen}
        onToggleExpand={this.toggleAdvancedFiltersExpand}
        titleTextKey="Filters.Advanced.Title"
      >
        <FilterField
          labelKey="Filters.Advanced.JobTitles.Name"
          tooltipKey="Filters.Advanced.JobTitles.Tooltip"
          filter={filterState.fields.jobTitles}
          multipleChoice
        >
          {filterState.fields.jobTitles.isLoading ? (
            <FilterLoadingSpinner />
          ) : (
            <CheckboxList
              options={filterState.fields.jobTitles.options}
              onChange={this.handleJobTitlesValueChange}
              scrollTop={filterState.fields.jobTitles.scrollTop}
              translateLabels
              sortAlphabetically
              includeSearch
            />
          )}
        </FilterField>
        <FilterField
          labelKey="Filters.Advanced.Generations.Name"
          tooltipKey="Filters.Advanced.Generations.Tooltip"
          filter={filterState.fields.generations}
          multipleChoice
        >
          {filterState.fields.generations.isLoading ? (
            <FilterLoadingSpinner />
          ) : (
            <CheckboxList
              options={filterState.fields.generations.options}
              onChange={this.handleGenerationsValueChange}
              scrollTop={filterState.fields.generations.scrollTop}
              translateLabels
            />
          )}
        </FilterField>
        {showGenderFilter && (
          <FilterField
            labelKey="Filters.Advanced.Gender.Name"
            tooltipKey="Filters.Advanced.Gender.Tooltip"
            filter={filterState.fields.genders}
            multipleChoice
          >
            {filterState.fields.genders.isLoading ? (
              <FilterLoadingSpinner />
            ) : (
              <CheckboxList
                options={filterState.fields.genders.options}
                onChange={this.handleGendersValueChange}
                scrollTop={filterState.fields.genders.scrollTop}
                translateLabels
              />
            )}
          </FilterField>
        )}        
        {userState.isAdmin &&
          (filterState.fields.managers.isLoading ||
            (filterState.fields.managers.options &&
              filterState.fields.managers.options.length > 0)) && (
            <FilterField
              labelKey="Filters.Advanced.Manager.Name"
              tooltipKey="Filters.Advanced.Manager.Tooltip"
              filter={filterState.fields.managers}
              multipleChoice
            >
              {filterState.fields.managers.isLoading ? (
                <FilterLoadingSpinner />
              ) : (
                filterState.activeField === "Filters.Advanced.Manager.Name" && (
                  <ManagerFilter
                    placeholder="Filters.Advanced.Manager.Placeholder"
                    filter={filterState.fields.managers.options}
                    onChange={this.handleManagerFilterChange}
                    itemSelected={filterState.fields.managers.selectedValue}
                  />
                )
              )}
            </FilterField>
          )}
      </FilterGroup>
    )

    return (
      <div className="side-filters">
        <div>
          <MainHeading primaryColor={userState.theme.primaryColor}>
            {t("Filters.Title")}
          </MainHeading>
          <ResetAllFiltersButton onClick={this.handleResetFiltersClick} />
          <div className="clearfix" />
        </div>
        {standardFilters}
        {advancedFilters}
        <UnPinFiltersButton onClick={() => toggleFiltersPinned(false)}>
          <i className="fa fa-angle-left" /> {t("Filters.SidebarToggleButton.Hide")}
        </UnPinFiltersButton>
        <MobileHideFiltersContainer mobileFiltersVisible={filterState.showFiltersFullScreen}>
          <button onClick={this.closeFilters}>{t("Filters.SidebarToggleButton.Close")}</button>
        </MobileHideFiltersContainer>
        <FooterLinkLeft>
          <hr />
          <Hyperlink href={ENVIRONMENT.getTalentToolboxUrl(userState.client.subdomain)}>
            <i className="mdi mdi-arrow-left" />
          </Hyperlink>
          <BackToTtLink theme={userState.theme} />
        </FooterLinkLeft>
      </div>
    )
  }
}

function mapStateToProps(state) {
  return {
    filterState: state.filters,
    userState: state.userDetail
  }
}

function mapDispatchToProps(dispatch) {
  return {
    resetFilters: () => dispatch(filterActions.resetAllFilters()),
    changeValueAppraisalLevels: (newValues, displayText, scrollTop) =>
      dispatch(filterActions.changeValueAppraisalLevels(newValues, displayText, scrollTop)),
    changeValueDataMode: newValue => dispatch(filterActions.changeValueDataMode(newValue)),
    changeValueEndDate: newValue => dispatch(filterActions.changeValueEndDate(newValue)),
    changeValueGenerations: (newValues, displayText, scrollTop) =>
      dispatch(filterActions.changeValueGenerations(newValues, displayText, scrollTop)),
    changeValueGenders: (newValues, displayText, scrollTop) =>
      dispatch(filterActions.changeValueGenders(newValues, displayText, scrollTop)),
    changeValueJobTitles: (newValues, displayText, scrollTop) =>
      dispatch(filterActions.changeValueJobTitles(newValues, displayText, scrollTop)),
    changeValueReview: newValue => dispatch(filterActions.changeValueReview(newValue)),
    changeValueStartDate: newValue => dispatch(filterActions.changeValueStartDate(newValue)),
    changeValueViewMode: newValue => dispatch(filterActions.changeValueViewMode(newValue)),
    configureViewModeForUser: user => dispatch(filterActions.configureViewModeForUser(user)),
    setAdvancedFiltersOpen: isOpen => dispatch(filterActions.setAdvancedFiltersOpen(isOpen)),
    changeManagerFilter: newValue => dispatch(filterActions.changeManagerFilter(newValue)),
    clearManagerFilter: () => dispatch(filterActions.clearManagerFilter()),
    toggleFullScreenFilters: isOpen => dispatch(filterActions.toggleFullScreenFilters(isOpen)),
    toggleFiltersPinned: isPinned => dispatch(filterActions.toggleFiltersPinned(isPinned))
  }
}

export default compose(translate(), connect(mapStateToProps, mapDispatchToProps))(ReportFilters)



// WEBPACK FOOTER //
// ./src/components/filters/ReportFilters.jsx